import { useState, useRef, useEffect } from 'react';
import { MapPin, Navigation } from 'lucide-react';
import { Button } from './Button';
import { cn } from '@/lib/cn';
import { useFilters, metersToMiles, milesToMeters, type LocationSource } from '@/contexts/FilterContext';

interface RadiusSliderProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
}

// Discrete mile values for the slider
// Half-mile increments up to 10, then larger steps
const MILE_STOPS = [
  0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5,
  5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10,
  12, 15, 20, 25,
];

function getMileStopIndex(meters: number): number {
  const miles = meters / 1609;
  // Find closest stop
  let closestIndex = 0;
  let closestDiff = Math.abs(MILE_STOPS[0] - miles);
  for (let i = 1; i < MILE_STOPS.length; i++) {
    const diff = Math.abs(MILE_STOPS[i] - miles);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIndex = i;
    }
  }
  return closestIndex;
}

function getLocationDisplayLabel(source: LocationSource, label: string | null, loading: boolean): string {
  if (loading) return 'Locating...';
  if (source === 'city' || source === 'custom') {
    return label || 'Custom';
  }
  return 'Near You';
}

export function RadiusSlider({ value, onChange, className }: RadiusSliderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [sliderIndex, setSliderIndex] = useState(() => getMileStopIndex(value));
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { locationSource, locationLabel, locationLoading, resetToGps } = useFilters();

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Sync slider with external value changes
  useEffect(() => {
    setSliderIndex(getMileStopIndex(value));
  }, [value]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(e.target.value, 10);
    setSliderIndex(index);
    const miles = MILE_STOPS[index];
    onChange(milesToMeters(miles));
  };

  const displayMiles = metersToMiles(value);
  const isUsingCity = locationSource === 'city' || locationSource === 'custom';
  const locationDisplay = getLocationDisplayLabel(locationSource, locationLabel, locationLoading);

  const handleResetToGps = () => {
    resetToGps();
  };

  return (
    <div className={cn('relative', className)}>
      <Button
        ref={buttonRef}
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={`Search radius: ${displayMiles} miles. ${isUsingCity ? `Location: ${locationDisplay}` : 'Near you'}. Click to adjust.`}
        className="flex items-center gap-1.5 px-3 border border-[var(--color-border)] bg-[var(--color-card)] max-w-[180px]"
      >
        <MapPin className="h-4 w-4 shrink-0" />
        {isUsingCity && <span className="truncate text-xs">{locationDisplay}</span>}
        <span className={isUsingCity ? "text-[var(--color-muted)] shrink-0" : ""}>{displayMiles} mi</span>
      </Button>

      {isOpen && (
        <div
          ref={dropdownRef}
          role="dialog"
          aria-label="Adjust search radius and location"
          className="absolute top-full right-0 mt-1 z-50 w-56 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4 shadow-xl"
        >
          <div className="space-y-4">
            {/* Location section - only show when viewing a city */}
            {isUsingCity && (
              <>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[var(--color-muted)]">Location</span>
                    <span className="font-medium truncate max-w-[120px]">{locationDisplay}</span>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResetToGps}
                    className="w-full flex items-center justify-center gap-2 text-xs"
                  >
                    <Navigation className="h-3.5 w-3.5" />
                    Use My Location
                  </Button>
                </div>

                {/* Divider */}
                <div className="border-t border-[var(--color-border)]" />
              </>
            )}

            {/* Radius section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--color-muted)]">Radius</span>
                <span className="font-medium">{MILE_STOPS[sliderIndex]} mi</span>
              </div>

              <input
                type="range"
                min={0}
                max={MILE_STOPS.length - 1}
                step={1}
                value={sliderIndex}
                onChange={handleSliderChange}
                aria-label="Search radius in miles"
                aria-valuemin={MILE_STOPS[0]}
                aria-valuemax={MILE_STOPS[MILE_STOPS.length - 1]}
                aria-valuenow={MILE_STOPS[sliderIndex]}
                aria-valuetext={`${MILE_STOPS[sliderIndex]} miles`}
                className="w-full h-2 bg-[var(--color-border)] rounded-lg appearance-none cursor-pointer accent-accent-primary"
              />

              <div className="flex justify-between text-xs text-[var(--color-muted)]">
                <span>0.5 mi</span>
                <span>25 mi</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
